import React, { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { Panel, TimeAgo } from "@/components/sov";
import { useAuth } from "@/contexts/AuthContext";
import {
  Box, Cpu, Lock, Unlock, ArrowRight, Plus, X, Zap, Send,
} from "lucide-react";

const fmt = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const HardwareIntegrity = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ amount_usd: "", source: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/hardware/integrity");
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const submitEntry = async (e) => {
    e.preventDefault();
    if (!form.amount_usd || !form.source) return;
    setSaving(true);
    try {
      await api.post("/hardware/ledger", {
        amount_usd: parseFloat(form.amount_usd),
        source: form.source,
        note: form.note || null,
      });
      setForm({ amount_usd: "", source: "", note: "" });
      setAdding(false);
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return (
      <Panel testid="panel-hardware-integrity" title="Hardware Integrity" code="§ HI">
        <div className="p-8 text-center"><span className="dot-loader"><span /><span /><span /></span></div>
      </Panel>
    );
  }

  const { virtual, physical, ledger, thresholds } = data;
  const ready = physical.ready;
  const pct = physical.progress_pct;

  return (
    <Panel
      testid="panel-hardware-integrity"
      title="Hardware Integrity"
      code="§ HI"
      action={
        <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-ivory-3 flex items-center gap-2">
          <Cpu size={11} className="text-gold-300" />
          virtual ↔ physical bridge
        </span>
      }
    >
      <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="hi-grid">
        {/* VIRTUAL BOX */}
        <div
          className="border border-[color:var(--gold-line)] p-5 relative overflow-hidden"
          style={{
            background: "linear-gradient(140deg, rgba(108,200,143,0.05), transparent 70%)",
            borderColor: virtual.active ? "rgba(108,200,143,0.45)" : "rgba(255,122,110,0.4)",
          }}
          data-testid="hi-virtual"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 border flex items-center justify-center"
                style={{ borderColor: "#6cc88f", boxShadow: "inset 0 0 12px rgba(108,200,143,0.18)" }}
              >
                <Zap size={18} style={{ color: "#6cc88f" }} />
              </div>
              <div>
                <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">State I</div>
                <div className="font-display text-[16px] tracking-[0.12em] text-gold-100 mt-0.5">
                  {virtual.label}
                </div>
              </div>
            </div>
            <span
              className="font-mono text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 border"
              style={{ color: "#6cc88f", borderColor: "rgba(108,200,143,0.5)" }}
              data-testid="hi-virtual-status"
            >
              ● Active
            </span>
          </div>

          <div className="font-serif italic text-[13px] text-ivory-2 mt-4 leading-snug">
            "{virtual.sublabel}"
          </div>

          <div className="grid grid-cols-3 gap-3 mt-5">
            <div>
              <div className="font-mono text-[9px] tracking-[0.18em] text-ivory-3 uppercase">Connectors</div>
              <div className="font-display text-[18px] text-gold-100 mt-0.5">
                {virtual.connectors_operational}/{virtual.connectors_total}
              </div>
            </div>
            <div>
              <div className="font-mono text-[9px] tracking-[0.18em] text-ivory-3 uppercase">Decks</div>
              <div className="font-display text-[18px] text-gold-100 mt-0.5">7</div>
            </div>
            <div>
              <div className="font-mono text-[9px] tracking-[0.18em] text-ivory-3 uppercase">Posture</div>
              <div className="font-display text-[18px] text-[#6cc88f] mt-0.5">{virtual.status}</div>
            </div>
          </div>
        </div>

        {/* PHYSICAL BOX */}
        <div
          className="border p-5 relative overflow-hidden"
          style={{
            background: ready
              ? "linear-gradient(140deg, rgba(220,185,95,0.10), transparent 70%)"
              : "linear-gradient(140deg, rgba(155,177,200,0.05), transparent 70%)",
            borderColor: ready ? "var(--gold-300)" : "rgba(155,177,200,0.3)",
            boxShadow: ready ? "0 0 24px rgba(220,185,95,0.25)" : "none",
          }}
          data-testid="hi-physical"
        >
          {ready && (
            <div
              className="absolute top-0 left-0 right-0 h-px"
              style={{ background: "linear-gradient(90deg, transparent, var(--gold-300), transparent)" }}
            />
          )}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 border flex items-center justify-center transition-all"
                style={{
                  borderColor: ready ? "var(--gold-300)" : "rgba(155,177,200,0.4)",
                  background: ready ? "rgba(220,185,95,0.08)" : "transparent",
                  boxShadow: ready ? "0 0 16px rgba(220,185,95,0.4)" : "none",
                }}
              >
                {ready ? <Unlock size={18} className="text-gold-200" /> : <Lock size={18} style={{ color: "#9bb1c8" }} />}
              </div>
              <div>
                <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">State II</div>
                <div className="font-display text-[16px] tracking-[0.12em] text-gold-100 mt-0.5" data-testid="hi-physical-label">
                  {physical.label}
                </div>
              </div>
            </div>
            <span
              className="font-mono text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 border"
              style={{
                color: ready ? "var(--gold-100)" : "#9bb1c8",
                borderColor: ready ? "var(--gold-300)" : "rgba(155,177,200,0.4)",
                background: ready ? "rgba(220,185,95,0.1)" : "transparent",
              }}
              data-testid="hi-physical-status"
            >
              {ready ? "⚜ READY" : "○ LOCKED"}
            </span>
          </div>

          <div className="font-serif italic text-[13px] text-ivory-2 mt-4 leading-snug">
            "{physical.sublabel}"
          </div>

          {/* PROGRESS */}
          <div className="mt-5">
            <div className="flex items-end justify-between mb-2">
              <div>
                <div className="font-mono text-[9px] tracking-[0.18em] text-ivory-3 uppercase">Forge Progress</div>
                <div className="flex items-baseline gap-2 mt-1">
                  <span
                    className="font-display text-[28px] gold-glow"
                    style={{ color: ready ? "var(--gold-100)" : "var(--gold-200)" }}
                    data-testid="hi-progress-pct"
                  >
                    {pct}%
                  </span>
                  <span className="font-mono text-[10px] text-ivory-3 tracking-[0.14em]">
                    {fmt(physical.revenue_usd)} / {fmt(thresholds.physical_kit_usd)}
                  </span>
                </div>
              </div>
              {!ready && (
                <div className="text-right">
                  <div className="font-mono text-[9px] tracking-[0.18em] text-ivory-3 uppercase">Remaining</div>
                  <div className="font-display text-[14px] text-gold-200 mt-1">{fmt(physical.remaining_usd)}</div>
                </div>
              )}
            </div>

            <div
              className="h-2 w-full bg-[var(--obsidian-3)] border border-[color:var(--gold-line)] relative overflow-hidden"
              data-testid="hi-progress-bar"
            >
              {/* Tick marks at 25/50/75 */}
              {[25, 50, 75].map((t) => (
                <span
                  key={t}
                  className="absolute top-0 bottom-0 w-px"
                  style={{ left: `${t}%`, background: "rgba(200,162,75,0.18)" }}
                />
              ))}
              <div
                className="h-full transition-all duration-700"
                style={{
                  width: `${pct}%`,
                  background: ready
                    ? "linear-gradient(90deg, var(--gold-400), var(--gold-200), var(--gold-100))"
                    : "linear-gradient(90deg, rgba(220,185,95,0.4), var(--gold-300))",
                  boxShadow: ready
                    ? "0 0 18px rgba(220,185,95,0.7), inset 0 0 6px rgba(255,255,255,0.15)"
                    : "0 0 8px rgba(220,185,95,0.3)",
                }}
              />
            </div>

            {/* milestones */}
            <div className="flex justify-between mt-1.5">
              {[0, 25, 50, 75, 100].map((t) => (
                <span
                  key={t}
                  className="font-mono text-[8px] tracking-[0.14em] uppercase"
                  style={{ color: pct >= t ? "var(--gold-200)" : "var(--ivory-3)" }}
                >
                  {t}%
                </span>
              ))}
            </div>
          </div>

          {ready && (
            <div className="mt-5 px-4 py-3 border border-[color:var(--gold-300)] bg-[rgba(220,185,95,0.08)]" data-testid="hi-ready-banner">
              <div className="flex items-center gap-2 mb-1">
                <Box size={13} className="text-gold-200" />
                <span className="font-display text-[12px] tracking-[0.22em] text-gold-100 uppercase">
                  Provisioning Authorized
                </span>
              </div>
              <p className="font-serif italic text-[12px] text-ivory-2">
                The hardware kit is forged and ready. Issue the provisioning order to bridge the Vessel into steel.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* LEDGER STRIP */}
      <div className="border-t border-[color:var(--gold-line)] px-5 py-4" data-testid="hi-ledger-strip">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[9px] tracking-[0.22em] text-gold-300 uppercase">§ HI.A</span>
            <span className="font-display text-[12px] tracking-[0.18em] text-gold-100 uppercase">Revenue Ledger</span>
            <span className="font-mono text-[9px] text-ivory-3 ml-1">
              · {ledger.entry_count} entries · total {fmt(ledger.total_usd)}
            </span>
          </div>
          {isAdmin && (
            <button
              onClick={() => setAdding(true)}
              className="btn-ghost"
              data-testid="hi-add-entry"
            >
              <Plus size={11} /> Log Entry
            </button>
          )}
        </div>

        {ledger.recent.length === 0 ? (
          <div className="font-serif italic text-[12px] text-ivory-3 py-4 text-center">
            No revenue logged yet. Bridge to steel begins with the first coin.
          </div>
        ) : (
          <div className="space-y-1.5">
            {ledger.recent.map((e) => (
              <div
                key={e.id}
                className="flex items-center justify-between px-3 py-2 border border-[color:var(--gold-line)] bg-[var(--obsidian-2)]/40"
                data-testid={`hi-entry-${e.id.slice(0,6)}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <ArrowRight size={11} className={e.amount_usd >= 0 ? "text-[#6cc88f]" : "text-[#ff7a6e]"} />
                  <span
                    className="font-display text-[14px]"
                    style={{ color: e.amount_usd >= 0 ? "var(--gold-100)" : "#ff9180" }}
                  >
                    {e.amount_usd >= 0 ? "+" : ""}{fmt(e.amount_usd)}
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.1em] text-ivory-2 truncate">
                    {e.source}
                  </span>
                  {e.note && (
                    <span className="font-serif italic text-[11px] text-ivory-3 truncate">— {e.note}</span>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-mono text-[9px] text-ivory-3">{e.actor}</span>
                  <TimeAgo iso={e.timestamp} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ADD MODAL */}
      {adding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" data-testid="hi-add-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setAdding(false)} />
          <form
            onSubmit={submitEntry}
            className="sov-panel sov-corners w-full max-w-md relative p-6 space-y-4"
            style={{ background: "var(--obsidian-2)" }}
          >
            <span className="corner tl" /><span className="corner tr" />
            <span className="corner bl" /><span className="corner br" />
            <div className="flex items-center justify-between">
              <h3 className="font-display text-[13px] tracking-[0.22em] text-gold-100 uppercase">
                Log Revenue Entry
              </h3>
              <button type="button" onClick={() => setAdding(false)} className="text-ivory-3 hover:text-gold-200">
                <X size={14} />
              </button>
            </div>

            <div>
              <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Amount (USD)</label>
              <input
                data-testid="hi-amount"
                type="number"
                step="0.01"
                required
                className="sov-input mt-1"
                placeholder="125.00"
                value={form.amount_usd}
                onChange={(e) => setForm({ ...form, amount_usd: e.target.value })}
              />
              <div className="font-mono text-[9px] text-ivory-3 mt-1">Negative for refund / cost.</div>
            </div>
            <div>
              <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Source</label>
              <input
                data-testid="hi-source"
                required
                className="sov-input mt-1"
                placeholder="e.g. Stripe / Manual / Audit Engagement #07"
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
              />
            </div>
            <div>
              <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Note (optional)</label>
              <input
                data-testid="hi-note"
                className="sov-input mt-1"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
              />
            </div>

            {err && <div className="font-mono text-[10px]" style={{ color: "#ff7a6e" }}>{err}</div>}

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setAdding(false)} className="btn-ghost">Cancel</button>
              <button type="submit" disabled={saving} className="btn-gold" data-testid="hi-save">
                <Send size={11} />
                {saving ? "Posting…" : "Post to Ledger"}
              </button>
            </div>
          </form>
        </div>
      )}
    </Panel>
  );
};

export default HardwareIntegrity;
